import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Zap, Wand2, Image as ImageIcon, Camera, ChefHat, ArrowRight, Check, Flame, Quote } from "lucide-react";
import Logo from "@/components/Logo";
import Powered360 from "@/components/Powered360";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/62e27dc3-7360-4fb7-ac75-decffa0363b4/images/f98d35b9447f9baeb0ab81acfc38c1e55d2d85974a62813762987c8bf462a2c0.png";
const BEFORE_IMG = "https://static.prod-images.emergentagent.com/jobs/62e27dc3-7360-4fb7-ac75-decffa0363b4/images/924e73fbd7d7ca40be1c58de99ccdb48b2693952562a4bd803d62ba6a44ca43f.png";
const AFTER_IMG = "https://static.prod-images.emergentagent.com/jobs/62e27dc3-7360-4fb7-ac75-decffa0363b4/images/f1842bb92996fa53770a5fe30f1445ffddf2226d9c92109734a9d62a0c8f0a88.png";
const CHEF_IMG = "https://static.prod-images.emergentagent.com/jobs/62e27dc3-7360-4fb7-ac75-decffa0363b4/images/8c3e0183c0ab8c2f6796229c5dd321a35129b24936a964360cdd53ca3852f5d0.png";
const EXEC_IMG = "https://static.prod-images.emergentagent.com/jobs/62e27dc3-7360-4fb7-ac75-decffa0363b4/images/c4be4bd54a4ede768f7b16a88d573b111e37c8018e692230fbb485809a98ba7b.png";
const FOOD_IMG_1 = "https://images.unsplash.com/photo-1622021142947-da7dedc7c39a?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85";
const FOOD_IMG_2 = "https://images.unsplash.com/photo-1590741664176-7fbd7e2592a0?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85";

const presets = [
    "Mexican", "Burger Joint", "Steakhouse", "Seafood", "Coffee Shop",
    "Bakery", "Cocktail Bar", "Fast Casual", "Fine Dining", "Tex-Mex", "Sushi", "Pizza",
];

const features = [
    { icon: Sparkles, title: "AI Photo Enhancer", desc: "Turn phone snaps into DSLR-quality shots in 8 seconds.", span: "md:col-span-7" },
    { icon: Wand2, title: "Background Generator", desc: "Swap any background — rustic wood, marble, dark moody, more.", span: "md:col-span-5" },
    { icon: MessageIcon, title: "Caption + Hashtags", desc: "Bilingual EN/ES captions that drive clicks & cravings.", span: "md:col-span-5" },
    { icon: ChefHat, title: "Restaurant Presets", desc: "12 cuisine-specific looks — sushi, steakhouse, taco, pizza.", span: "md:col-span-7" },
];

function MessageIcon(props) {
    return <Sparkles {...props} />;
}

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.21, 0.47, 0.32, 0.98] } }),
};

export default function Landing() {
    const navigate = useNavigate();
    return (
        <div className="min-h-screen bg-zinc-950 text-zinc-100 grain">
            {/* NAV */}
            <header className="fixed top-0 inset-x-0 z-40 bg-zinc-950/80 backdrop-blur-xl border-b border-white/5">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Logo size="md" />
                        <span className="hidden sm:block h-5 w-px bg-white/10" />
                        <Powered360 variant="pill" className="hidden sm:inline-flex" />
                    </div>
                    <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
                        <a href="#features" className="hover:text-white transition">Features</a>
                        <a href="#showcase" className="hover:text-white transition">Showcase</a>
                        <a href="#pricing" className="hover:text-white transition">Pricing</a>
                        <a href="#faq" className="hover:text-white transition">FAQ</a>
                    </nav>
                    <div className="flex items-center gap-3">
                        <Link to="/login" data-testid="nav-login-link" className="text-sm text-zinc-300 hover:text-white transition">Login</Link>
                        <Link to="/signup" data-testid="nav-signup-btn" className="btn-magic text-sm font-semibold text-white px-4 py-2 rounded-full">
                            Start free
                        </Link>
                    </div>
                </div>
            </header>

            {/* HERO */}
            <section className="relative pt-32 pb-24 overflow-hidden">
                <div
                    className="absolute inset-0 -z-10 bg-cover bg-center"
                    style={{
                        backgroundImage: `linear-gradient(180deg, rgba(9,9,11,0.75) 0%, rgba(9,9,11,0.9) 100%), url(${HERO_BG})`,
                    }}
                />
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-10 items-center">
                    <motion.div
                        initial="hidden"
                        animate="show"
                        variants={fadeUp}
                        className="md:col-span-7"
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-zinc-300 mb-7">
                            <Flame className="w-3.5 h-3.5 text-orange-400" />
                            <span className="font-mono tracking-widest uppercase text-[10px]">360 Restaurant Growth Engine</span>
                        </div>
                        <h1 className="font-heading text-5xl md:text-7xl font-bold tracking-tighter leading-[1.02]">
                            Food photos that <span className="text-gradient-magic">actually sell.</span>
                        </h1>
                        <p className="mt-7 max-w-xl text-lg text-zinc-400 leading-relaxed">
                            Drop in a phone snap. Get a studio-quality marketing image, social caption,
                            and menu-ready shot in 8 seconds — built for restaurants, by restaurant operators.
                        </p>
                        <div className="mt-9 flex flex-wrap items-center gap-4">
                            <button
                                type="button"
                                onClick={() => navigate("/signup")}
                                data-testid="hero-cta-signup"
                                className="btn-magic px-7 py-3.5 rounded-full text-white font-semibold flex items-center gap-2"
                            >
                                Start free trial
                                <ArrowRight className="w-4 h-4" />
                            </button>
                            <a
                                href="#showcase"
                                data-testid="hero-cta-demo"
                                className="px-6 py-3.5 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 transition text-white font-medium"
                            >
                                See the magic
                            </a>
                        </div>
                        <div className="mt-10 flex items-center gap-7 text-xs text-zinc-500">
                            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-orange-400" /> 10 free credits</div>
                            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-orange-400" /> No card required</div>
                            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-orange-400" /> Cancel anytime</div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        animate="show"
                        custom={1}
                        variants={fadeUp}
                        className="md:col-span-5 relative"
                    >
                        <div className="relative rounded-3xl overflow-hidden border border-white/10 glow-orange">
                            <BeforeAfterSlider beforeSrc={BEFORE_IMG} afterSrc={AFTER_IMG} />
                        </div>
                        <div className="absolute -bottom-5 -left-5 px-3 py-2 rounded-2xl glass-card text-xs font-mono uppercase tracking-widest text-zinc-300">
                            ◢ 8s processing
                        </div>
                    </motion.div>
                </div>

                {/* Logo strip */}
                <div className="mt-24 max-w-6xl mx-auto px-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 opacity-60">
                    {["Cravin'Grub", "TacoLoco", "Marble & Smoke", "Coastal Plates", "Hearth Bakery", "Tap & Pour"].map((b) => (
                        <span key={b} className="text-zinc-500 font-heading text-lg tracking-tight">{b}</span>
                    ))}
                </div>
            </section>

            {/* FEATURES BENTO */}
            <section id="features" className="py-28 max-w-7xl mx-auto px-6">
                <div className="mb-16 max-w-2xl">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-orange-400 mb-3 font-mono">Features</div>
                    <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
                        Built for restaurants, <br />not creatives.
                    </h2>
                    <p className="mt-5 text-zinc-400 text-lg leading-relaxed">
                        Every tool is shaped around how busy operators actually post. Upload, transform, publish — without opening Photoshop.
                    </p>
                </div>

                <div className="grid md:grid-cols-12 gap-6">
                    {features.map((f, i) => (
                        <motion.div
                            key={f.title}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: "-100px" }}
                            custom={i}
                            variants={fadeUp}
                            className={`glass-card lift rounded-3xl p-7 md:p-9 ${f.span}`}
                        >
                            <div className="h-10 w-10 grid place-items-center rounded-xl bg-orange-500/15 border border-orange-500/20 text-orange-400 mb-5">
                                <f.icon className="w-5 h-5" />
                            </div>
                            <h3 className="font-heading text-xl md:text-2xl font-semibold tracking-tight">{f.title}</h3>
                            <p className="mt-2 text-zinc-400">{f.desc}</p>
                        </motion.div>
                    ))}

                    {/* Preset chips card */}
                    <motion.div
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true }}
                        variants={fadeUp}
                        className="md:col-span-12 glass-card rounded-3xl p-7 md:p-9"
                    >
                        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                            <h3 className="font-heading text-2xl font-semibold tracking-tight">
                                12 cuisine-tuned presets
                            </h3>
                            <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">No prompt skills needed</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {presets.map((p) => (
                                <span key={p} className="px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-zinc-300 hover:bg-orange-500/10 hover:border-orange-500/30 transition cursor-default">
                                    {p}
                                </span>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* SHOWCASE */}
            <section id="showcase" className="py-28 max-w-7xl mx-auto px-6">
                <div className="grid md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-5">
                        <div className="text-[11px] uppercase tracking-[0.22em] text-orange-400 mb-3 font-mono">Live demo</div>
                        <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
                            From phone shot <br /> to <span className="text-gradient-magic">profit shot.</span>
                        </h2>
                        <p className="mt-5 text-zinc-400 text-lg leading-relaxed">
                            Drag the slider. The left is your phone today. The right is your menu, your DoorDash thumbnail,
                            your Instagram tomorrow — generated in seconds.
                        </p>
                        <ul className="mt-7 space-y-3 text-zinc-300">
                            {["Lighting & white balance fix", "Realistic depth & shadows", "Color grading for cravings", "Texture & garnish enhancement"].map((s) => (
                                <li key={s} className="flex items-center gap-3">
                                    <span className="h-1.5 w-1.5 rounded-full bg-orange-400" />
                                    <span>{s}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="md:col-span-7">
                        <div className="rounded-3xl overflow-hidden border border-white/10 glow-orange">
                            <BeforeAfterSlider beforeSrc={BEFORE_IMG} afterSrc={AFTER_IMG} />
                        </div>
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="py-28 bg-zinc-900/40 border-y border-white/5">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-14">
                        <div className="text-[11px] uppercase tracking-[0.22em] text-orange-400 mb-3 font-mono">Trusted by operators</div>
                        <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
                            What restaurants are saying.
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            { img: CHEF_IMG, name: "Marco Salazar", role: "Owner, Tacos del Norte", quote: "Our DoorDash conversions jumped 38% in two weeks. The team posts daily now — they don't dread it." },
                            { img: EXEC_IMG, name: "Priya Anand", role: "VP Marketing, Hearth Group", quote: "We replaced our $5k/month food photographer for casual menu shots. The AI is genuinely indistinguishable." },
                            { img: FOOD_IMG_1, name: "Chef Daniel Rios", role: "Exec Chef, Marble & Smoke", quote: "It nails our brand look — moody, premium, mouth-watering. Even our Michelin reviewer noticed." },
                        ].map((t) => (
                            <div key={t.name} className="glass-card rounded-3xl p-7 lift">
                                <Quote className="w-6 h-6 text-orange-400 mb-4" />
                                <p className="text-zinc-200 leading-relaxed">{t.quote}</p>
                                <div className="mt-6 flex items-center gap-3">
                                    <img src={t.img} alt={t.name} className="w-11 h-11 rounded-full object-cover border border-white/10" />
                                    <div>
                                        <div className="text-sm font-semibold">{t.name}</div>
                                        <div className="text-xs text-zinc-500">{t.role}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PRICING TEASER */}
            <section id="pricing" className="py-28 max-w-7xl mx-auto px-6">
                <div className="text-center mb-14">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-orange-400 mb-3 font-mono">Pricing</div>
                    <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">Built for every kitchen.</h2>
                    <p className="mt-5 text-zinc-400 text-lg">Replace your photographer, not your style. Start free.</p>
                </div>
                <div className="grid md:grid-cols-3 gap-6">
                    {[
                        { name: "DIY", price: 29, credits: 100, features: ["100 AI enhancements/mo", "All preset styles", "Caption generator", "Image library"] },
                        { name: "Restaurant Pro", price: 99, credits: 500, popular: true, features: ["500 AI enhancements/mo", "Background generator", "Brand kit", "Priority processing"] },
                        { name: "Agency", price: 299, credits: 2000, features: ["2000 AI enhancements/mo", "Multi-restaurant brand kits", "Priority support", "White-label option"] },
                    ].map((p) => (
                        <div key={p.name} className={`rounded-3xl p-8 border transition lift ${p.popular ? "border-orange-500/40 bg-gradient-to-b from-orange-500/8 to-zinc-900/40" : "border-white/8 bg-zinc-900/40"}`}>
                            {p.popular && (
                                <div className="inline-block text-[10px] font-mono uppercase tracking-widest text-orange-300 bg-orange-500/15 px-2 py-1 rounded-full mb-4">Most popular</div>
                            )}
                            <h3 className="font-heading text-2xl font-bold">{p.name}</h3>
                            <div className="mt-3 mb-6">
                                <span className="font-heading text-5xl font-bold">${p.price}</span>
                                <span className="text-zinc-500 ml-1">/mo</span>
                            </div>
                            <ul className="space-y-3 mb-8">
                                {p.features.map((f) => (
                                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                                        <Check className="w-4 h-4 mt-0.5 text-orange-400 shrink-0" />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <Link
                                to="/signup"
                                data-testid={`pricing-cta-${p.name.toLowerCase().replace(" ", "-")}`}
                                className={`block text-center w-full py-3 rounded-full font-semibold transition ${p.popular ? "btn-magic text-white" : "bg-white/5 hover:bg-white/10 text-white border border-white/10"}`}
                            >
                                Start {p.name}
                            </Link>
                        </div>
                    ))}
                </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="py-28 max-w-3xl mx-auto px-6">
                <div className="text-center mb-12">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-orange-400 mb-3 font-mono">FAQ</div>
                    <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">Common questions.</h2>
                </div>
                <div className="space-y-4">
                    {[
                        { q: "Will it still look like my food?", a: "Yes. We preserve the dish, plating and brand. We elevate lighting, color, texture, and background — not the recipe." },
                        { q: "Is this legal to use as marketing?", a: "Absolutely. You own all generated images. AI editing of your own dish photos is industry-standard for restaurant marketing." },
                        { q: "How does it compare to a food photographer?", a: "Photographers cost $400-$1500 per shoot. We do unlimited shots, in 8 seconds, from a phone snap. Most chains use us alongside one yearly hero shoot." },
                        { q: "What plans can my agency manage?", a: "The Agency plan lets you manage multiple restaurant brand kits, switching between them per upload. White-label is available." },
                    ].map((f) => (
                        <details key={f.q} className="glass-card rounded-2xl p-5 group" data-testid="faq-item">
                            <summary className="cursor-pointer flex items-center justify-between text-base font-semibold">
                                {f.q}
                                <span className="text-orange-400 group-open:rotate-45 transition">+</span>
                            </summary>
                            <p className="mt-3 text-zinc-400">{f.a}</p>
                        </details>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="py-28 px-6">
                <div className="max-w-5xl mx-auto relative rounded-[2rem] overflow-hidden border border-white/10 grain">
                    <div className="absolute inset-0 -z-10 bg-cover bg-center" style={{ backgroundImage: `linear-gradient(135deg, rgba(225,29,72,0.4), rgba(249,115,22,0.4)), url(${FOOD_IMG_2})` }} />
                    <div className="px-8 md:px-16 py-20 text-center">
                        <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-tighter">
                            Your food deserves <br className="hidden md:block" />
                            <span className="text-gradient-soft">better photos.</span>
                        </h2>
                        <p className="mt-6 text-zinc-200/90 text-lg max-w-xl mx-auto">
                            Start with 10 free enhancements. No credit card. See the difference in your first upload.
                        </p>
                        <Link
                            to="/signup"
                            data-testid="cta-final-signup"
                            className="mt-9 inline-flex items-center gap-2 btn-magic px-8 py-4 rounded-full text-white font-semibold"
                        >
                            Claim free credits <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="border-t border-white/5 py-12 px-6">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div className="flex items-center gap-5">
                        <Logo size="sm" />
                        <span className="h-5 w-px bg-white/10" />
                        <Powered360 variant="inline" />
                    </div>
                    <div className="text-xs text-zinc-500">
                        © {new Date().getFullYear()} FoodGlow360 — part of the 360 Restaurant Growth Engine.
                    </div>
                    <div className="flex items-center gap-5 text-xs text-zinc-400">
                        <a href="#features" className="hover:text-white">Features</a>
                        <a href="#pricing" className="hover:text-white">Pricing</a>
                        <a href="#faq" className="hover:text-white">FAQ</a>
                    </div>
                </div>
            </footer>
        </div>
    );
}
